<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query';
import { api } from '../api/client';
import ApiErrorPanel from '../components/ApiErrorPanel.vue';
import LoadingPanel from '../components/LoadingPanel.vue';
import StatusBadge from '../components/StatusBadge.vue';
import AppIcon from '../components/AppIcon.vue';

const capabilities = useQuery({
  queryKey: ['capabilities'],
  queryFn: api.capabilities,
});
const status = useQuery({
  queryKey: ['component-status'],
  queryFn: api.status,
  refetchInterval: 30_000,
});
</script>

<template>
  <section>
    <header class="page-header">
      <div>
        <p class="eyebrow">{{ $t('system.eyebrow') }}</p>
        <h1>{{ $t('system.title') }}</h1>
        <p>{{ $t('system.description') }}</p>
      </div>
      <StatusBadge v-if="status.data.value" :status="status.data.value.status" />
    </header>
    <LoadingPanel v-if="status.isPending.value" :label="$t('system.checking')" />
    <ApiErrorPanel
      v-else-if="status.error.value"
      :error="status.error.value"
      @retry="status.refetch()"
    />
    <section v-else class="panel">
      <div class="component-grid">
        <article v-for="(value, name) in status.data.value?.components" :key="name">
          <div>
            <strong>{{ name }}</strong>
            <small>{{ $t('system.required') }}</small>
          </div>
          <StatusBadge :status="value" />
        </article>
      </div>
    </section>
    <LoadingPanel v-if="capabilities.isPending.value" :label="$t('system.loadingCapabilities')" />
    <ApiErrorPanel
      v-else-if="capabilities.error.value"
      :error="capabilities.error.value"
      @retry="capabilities.refetch()"
    />
    <section v-else class="panel">
      <div class="section-heading">
        <div>
          <p class="eyebrow">API {{ capabilities.data.value?.api_version }}</p>
          <h2>{{ $t('system.capabilities') }}</h2>
        </div>
      </div>
      <div class="capability-list">
        <article v-for="(enabled, name) in capabilities.data.value?.features" :key="name">
          <span class="capability-list__name">
            <AppIcon :name="enabled ? 'check' : 'blocked'" :size="16" />
            {{ name.replaceAll('_', ' ') }}
          </span>
          <StatusBadge :status="enabled ? 'enabled' : 'disabled'" />
        </article>
      </div>
      <div class="search-capability">
        <div class="search-capability__heading">
          <AppIcon name="search" :size="18" />
          <strong>{{ $t('system.indexedFields') }}</strong>
        </div>
        <div class="search-capability__fields">
          <code v-for="field in capabilities.data.value?.search.fields" :key="field">{{
            field
          }}</code>
        </div>
        <p>{{ $t('system.searchLimit') }}</p>
      </div>
    </section>
  </section>
</template>
